import { FormieInterface, FormieLoadedEvent } from '~/types/Formie'

/**
 * The Formie JS instance.
 */
export let Formie: FormieInterface | null = null;
// This queue holds methods that are called before Formie is loaded.
const queue: string[] = [];

/**
 * Handle the Formie load event and initialize Formie forms.
 * @see src/templates/_layouts/skeleton.twig
 * @see src/assets/js/main.ts
 *
 * @param formie - The Formie instance.
 */
export function handleFormieLoad(formie): void {
  Formie = formie;
  runQueue();
  initFormieForms();
  // Refresh CSRF tokens per-form as each one initializes (needed for static cache)
  document.addEventListener('onFormieInit', (event: Event) => {
    const { formId } = (event as CustomEvent<{ formId: string }>).detail;
    // Formie logs failures itself; catch so a failed token fetch isn't an unhandled rejection
    Formie?.refreshForCache(formId)?.catch(() => {});
  });
}

/**
 * Initialize Formie forms on the page as soon as Formie is loaded.
 */
export function initFormieForms(): void {
  if (!Formie) {
    queue.push('initForms');
    return;
  }
  Formie.initForms();
}

/**
 * Destroy all Formie forms so they can be cleanly re-initialized after a Swup page swap.
 *
 * Formie appends each form's registered JS (captchas, etc.) to <body>, outside of Swup's
 * containers. If those aren't removed, re-initializing the same form on back/forward
 * navigation bails early ("scripts already loaded") and e.g. Turnstile never renders.
 */
export async function destroyFormieForms(): Promise<void> {
  // Nothing was initialized yet, so nothing to tear down
  const formie = Formie;
  if (!formie?.forms) {
    return;
  }

  // Copy the array (destroyForm splices it) and settle all so one failing form can't block the rest
  const results = await Promise.allSettled([...formie.forms].map((form) => formie.destroyForm(form)));
  results.forEach((result) => {
    if (result.status === 'rejected') {
      console.error(`[Formie] ${result.reason}`);
    }
  });
}

/**
 * Run all queued methods that were called before Formie was loaded.
 */
function runQueue() {
  console.log('[Formie] Running queued methods:', queue);
  queue.forEach((method) => {
    try {
      if (Formie && typeof Formie[method as keyof FormieInterface] === 'function') {
        (Formie[method as keyof FormieInterface] as Function)();
      }
    } catch (err) {
      console.error(`[Formie] ${err}`);
    }
  });
}
